import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { theme } from './theme.js';
import { css, smoothTransition } from './styles.js';
const MOBILE_BREAKPOINT = 768;
function useIsMobile() {
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false);
    useEffect(() => {
        const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
        const handler = (e) => setIsMobile(e.matches);
        mq.addEventListener('change', handler);
        setIsMobile(mq.matches);
        return () => mq.removeEventListener('change', handler);
    }, []);
    return isMobile;
}
export const Layout = ({ children, sidebar, topBar, sidebarCollapsible = true, sidebarOpen: externalSidebarOpen, onSidebarToggle, labels, className, style, }) => {
    // English defaults — consumers pass translated strings via the `labels` prop.
    const skipToMainText = labels?.skipToMain ?? 'Skip to main content';
    const mainNavAria = labels?.mainNavAria ?? 'Main navigation';
    const collapseSidebarAria = labels?.collapseSidebarAria ?? 'Collapse sidebar';
    const closeMenuAria = labels?.closeMenuAria ?? 'Close menu';
    const openMenuAria = labels?.openMenuAria ?? 'Open menu';
    const isMobile = useIsMobile();
    const [internalSidebarOpen, setInternalSidebarOpen] = useState(!isMobile);
    const sidebarOpen = externalSidebarOpen !== undefined ? externalSidebarOpen : internalSidebarOpen;
    // Auto-close sidebar when switching to mobile
    useEffect(() => {
        if (isMobile && sidebarOpen) {
            setInternalSidebarOpen(false);
            onSidebarToggle?.(false);
        }
    }, [isMobile, sidebarOpen, onSidebarToggle]);
    const handleToggleSidebar = (open) => {
        setInternalSidebarOpen(open);
        onSidebarToggle?.(open);
    };
    const layoutStyle = css({
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        backgroundColor: theme.colors.bg.primary,
        color: theme.colors.text.primary,
        fontFamily: theme.typography.fontFamily.primary,
        overflow: 'hidden',
    }, style);
    const topBarStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '64px',
        padding: `0 ${theme.spacing.lg}`,
        backgroundColor: theme.colors.bg.secondary,
        borderBottom: `1px solid ${theme.colors.border.secondary}`,
        flexShrink: 0,
    };
    const contentContainerStyle = {
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
        position: 'relative',
    };
    // On mobile: sidebar is a fixed overlay drawer
    // On desktop: sidebar is an inline panel
    const sidebarStyle = isMobile
        ? {
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            width: '280px',
            backgroundColor: theme.colors.bg.secondary,
            borderRight: `1px solid ${theme.colors.border.secondary}`,
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1000,
            transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform 250ms ease',
            boxShadow: sidebarOpen ? '4px 0 24px rgba(0,0,0,0.5)' : 'none',
        }
        : {
            width: sidebarOpen ? '240px' : '0px',
            minWidth: sidebarOpen ? '240px' : '0px',
            backgroundColor: theme.colors.bg.secondary,
            borderRight: sidebarOpen ? `1px solid ${theme.colors.border.secondary}` : 'none',
            overflowY: 'auto',
            overflowX: 'hidden',
            // `visibility` is delayed by the animation's duration so the fade still
            // plays, then takes effect. Width and opacity alone leave a collapsed
            // sidebar INVISIBLE BUT PRESENT: every link stays in the tab order and
            // in the accessibility tree, so a keyboard or screen-reader user walks
            // into a menu that is not on screen and cannot be seen to have focus.
            //
            // It also makes the collapse observable. `innerText` ignores
            // visibility:hidden content but not opacity:0 content, so before this
            // the real-click crawler saw an identical page before and after and
            // reported the control as doing nothing on every route (C38). The
            // control worked; nothing could tell.
            transition: sidebarOpen
                ? 'width 200ms ease, min-width 200ms ease, opacity 200ms ease, visibility 0s'
                : 'width 200ms ease, min-width 200ms ease, opacity 200ms ease, visibility 0s linear 200ms',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            opacity: sidebarOpen ? 1 : 0,
            visibility: sidebarOpen ? 'visible' : 'hidden',
            position: 'relative',
        };
    const mainContentStyle = {
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
    };
    // Desktop collapse toggle pinned to the sidebar edge
    const toggleStyle = {
        position: 'absolute',
        top: '18px',
        right: '-14px',
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        backgroundColor: theme.colors.bg.tertiary,
        border: `1px solid ${theme.colors.border.secondary}`,
        color: theme.colors.text.muted,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        transition: smoothTransition().transition,
        zIndex: 10,
        padding: 0,
    };
    // Hamburger button for mobile + collapsed desktop
    const hamburgerStyle = {
        position: 'fixed',
        top: '12px',
        left: '12px',
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        backgroundColor: theme.colors.bg.secondary,
        border: `1px solid ${theme.colors.border.secondary}`,
        color: theme.colors.text.secondary,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        transition: smoothTransition().transition,
        zIndex: 999,
        padding: 0,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    };
    // Backdrop overlay for mobile drawer
    const backdropStyle = {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        zIndex: 999,
        opacity: sidebarOpen ? 1 : 0,
        pointerEvents: sidebarOpen ? 'auto' : 'none',
        transition: 'opacity 250ms ease',
    };
    // KI-026 — "Skip to main content" link. Visually hidden until focused via
    // keyboard Tab. The first focusable element on the page so screen readers
    // and keyboard users can bypass nav directly to the page content.
    const skipLinkStyle = {
        position: 'absolute',
        left: '8px',
        top: '8px',
        padding: '8px 16px',
        background: theme.colors.accent.primary,
        color: '#fff',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 600,
        textDecoration: 'none',
        zIndex: 9999,
        // Hidden until focused
        transform: 'translateY(-150%)',
        opacity: 0,
        transition: 'transform 150ms ease, opacity 150ms ease',
        pointerEvents: 'none',
    };
    const skipLinkFocusStyle = {
        transform: 'translateY(0)',
        opacity: 1,
        pointerEvents: 'auto',
    };
    return (_jsxs("div", { style: layoutStyle, className: className, children: [_jsx("a", { href: "#main-content", style: skipLinkStyle, onFocus: (e) => Object.assign(e.currentTarget.style, skipLinkFocusStyle), onBlur: (e) => Object.assign(e.currentTarget.style, skipLinkStyle), children: skipToMainText }), topBar && _jsx("div", { style: topBarStyle, children: topBar }), _jsxs("div", { style: contentContainerStyle, children: [isMobile && sidebar && (_jsx("div", { style: backdropStyle, onClick: () => handleToggleSidebar(false), "aria-hidden": "true" })), sidebar && (_jsxs("aside", { style: sidebarStyle, children: [_jsx("nav", { "aria-label": mainNavAria, style: {
                                    flex: 1,
                                    padding: '16px 12px',
                                    minWidth: '216px',
                                }, children: sidebar }), !isMobile && sidebarCollapsible && sidebarOpen && (_jsx("button", { onClick: () => handleToggleSidebar(false), style: toggleStyle, "aria-label": collapseSidebarAria, title: collapseSidebarAria, children: _jsx("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", children: _jsx("path", { d: "M15 18l-6-6 6-6", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }) })), isMobile && sidebarOpen && (_jsx("button", { onClick: () => handleToggleSidebar(false), style: {
                                    position: 'absolute',
                                    top: '12px',
                                    right: '12px',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '8px',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    color: theme.colors.text.muted,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 0,
                                    zIndex: 10,
                                }, "aria-label": closeMenuAria, children: _jsx("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", children: _jsx("path", { d: "M18 6L6 18M6 6l12 12", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" }) }) }))] })), sidebarCollapsible && !sidebarOpen && (_jsx("button", { onClick: () => handleToggleSidebar(true), style: hamburgerStyle, "aria-label": openMenuAria, title: openMenuAria, children: _jsx("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", children: _jsx("path", { d: "M3 12h18M3 6h18M3 18h18", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" }) }) })), _jsx("main", { id: "main-content", style: mainContentStyle, tabIndex: -1, children: children })] })] }));
};
//# sourceMappingURL=Layout.js.map